import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import { getAudit, rescanAudit, unwrapGet, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AUDIT_STATUSES } from "../constants";
import { frameworkLabel, scorePercent } from "../utils";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_BORDER, AH_MUTED, AH_TEXT, AH_WHITE, outlinedButtonSx } from "../theme";
import AddAccountDialog from "./AddAccountDialog";
import RemoveScopeDialog from "./RemoveScopeDialog";

/** There is no scan-status route; scores move when `latestRunId` reaches the triggered run. */
const SCAN_POLL_MS = 20000;
const SCAN_POLL_LIMIT_MS = 30 * 60 * 1000;

const LABEL_COLOR = "#334155";

const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  flexShrink: 0,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#334155" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9", backgroundColor: "#f8fafc" },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
};

const headerButtonSx = {
  ...outlinedButtonSx,
  height: 32,
  minHeight: 32,
  fontSize: "0.8125rem",
  color: AH_ACCENT,
  borderColor: AH_ACCENT,
  "&:hover": {
    color: AH_ACCENT_HOVER,
    borderColor: AH_ACCENT_HOVER,
    backgroundColor: "#fff4eb",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: AH_ACCENT,
    borderColor: AH_ACCENT,
    opacity: 0.5,
  },
};

const scopeChipSx = {
  height: 26,
  maxWidth: "100%",
  borderRadius: "6px",
  backgroundColor: "#fff4eb",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: "0.8125rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
  "& .MuiChip-deleteIcon": {
    color: "#9a3412",
    fontSize: 16,
    "&:hover": { color: "#dc2626" },
  },
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const accountLabelOf = (account) => account?.accountName || account?.accountNumber || "Account";

const scopeTooltip = (scope) => {
  const percent = scope.hasScanResult ? scorePercent(scope.auditScore) : null;
  const score = percent == null ? "Not yet scored" : `Score ${Math.round(percent)}%`;
  const total = Number(scope.progress?.total);
  const decided = Number(scope.progress?.decided) || 0;
  return Number.isFinite(total) && total > 0 ? `${score} · ${decided} of ${total} manual controls decided` : score;
};

/** Messages go to `onNotice` as `{ severity, message }`, or `null` to clear; the dialog shows them in its footer. */
const AuditScopeSection = ({ auditId, auditStatus, initialAccounts, disabled, onChanged, onNotice }) => {
  const [accounts, setAccounts] = useState(Array.isArray(initialAccounts) ? initialAccounts : []);
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;
  const setNotice = useCallback((notice) => {
    if (onNoticeRef.current) onNoticeRef.current(notice);
  }, []);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [rescanning, setRescanning] = useState(null);
  const [scanning, setScanning] = useState({});
  const scanStartedAt = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const editable = auditStatus === AUDIT_STATUSES.DRAFT;
  const canRescan = auditStatus === AUDIT_STATUSES.ACTIVE;
  const busy = disabled || Boolean(rescanning);

  useEffect(() => {
    setAccounts(Array.isArray(initialAccounts) ? initialAccounts : []);
  }, [initialAccounts]);

  /** An audit covers one framework; a new account joins on it rather than picking its own. */
  const auditFrameworks = useMemo(() => {
    const byId = new Map();
    accounts.forEach((account) => {
      (Array.isArray(account.scopes) ? account.scopes : []).forEach((scope) => {
        const id = Number(scope.framework?.complianceAddonId);
        if (Number.isInteger(id) && !byId.has(id)) byId.set(id, scope.framework);
      });
    });
    return [...byId.values()];
  }, [accounts]);

  const reload = useCallback(async () => {
    try {
      const detail = unwrapGet(await getAudit(auditId));
      setAccounts(Array.isArray(detail.accounts) ? detail.accounts : []);
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to refresh this assignment's scope." });
    }
  }, [auditId, setNotice]);

  useEffect(() => {
    const pending = Object.entries(scanning);
    if (!pending.length) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      if (Date.now() - scanStartedAt.current > SCAN_POLL_LIMIT_MS) {
        setScanning({});
        return;
      }
      try {
        const detail = unwrapGet(await getAudit(auditId));
        if (!active) return;
        const list = Array.isArray(detail.accounts) ? detail.accounts : [];
        setAccounts(list);
        const finished = pending
          .filter(([accountId, runId]) => {
            const account = list.find((item) => item.auditAccountId === accountId);
            const scopes = Array.isArray(account?.scopes) ? account.scopes : [];
            return !scopes.length || scopes.some((scope) => scope.latestRunId === runId);
          })
          .map(([accountId]) => accountId);
        if (finished.length) {
          setScanning((prev) => {
            const next = { ...prev };
            finished.forEach((accountId) => delete next[accountId]);
            return next;
          });
          if (onChangedRef.current) onChangedRef.current();
        }
      } catch (_error) {
        // The next tick retries; a failed poll must not end the scan indicator.
      }
    }, SCAN_POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [scanning, auditId]);

  const afterChange = (message) => {
    setNotice({ severity: "success", message });
    reload();
    if (onChanged) onChanged();
  };

  const rescan = async (account) => {
    if (busy) return;
    setRescanning(account?.auditAccountId || "all");
    setNotice(null);
    try {
      const result = unwrapPost(await rescanAudit(auditId, account?.auditAccountId));
      const runs = Array.isArray(result?.runs) ? result.runs : [];
      const failures = Array.isArray(result?.dispatchFailures) ? result.dispatchFailures.filter(Boolean) : [];
      if (runs.length) {
        scanStartedAt.current = Date.now();
        setScanning((prev) => {
          const next = { ...prev };
          runs.forEach((run) => {
            if (run.auditAccountId && run.runId) next[run.auditAccountId] = run.runId;
          });
          return next;
        });
      }
      setNotice(
        failures.length
          ? { severity: "warning", message: `Scan dispatch: ${failures.join(" ")}` }
          : {
              severity: "success",
              message: `Scan started for ${plural(runs.length, "account")}. Scores update as each scan completes.`,
            }
      );
      if (onChanged) onChanged();
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to start the scan." });
    } finally {
      setRescanning(null);
    }
  };

  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}>Scope</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#74788d", lineHeight: 1.45 }}>
            {editable
              ? "Changes to accounts apply immediately."
              : "Accounts and frameworks can be changed only while the assignment is a draft."}
          </Typography>
        </Box>
        {editable || (canRescan && accounts.length) ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {editable ? (
              <Button
                variant="outlined"
                onClick={() => setAddAccountOpen(true)}
                disabled={busy}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={headerButtonSx}
              >
                Add Account
              </Button>
            ) : null}
            {canRescan && accounts.length ? (
              <Button
                variant="outlined"
                onClick={() => rescan(null)}
                disabled={busy}
                startIcon={
                  rescanning === "all" ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 18 }} />
                }
                sx={headerButtonSx}
              >
                {rescanning === "all" ? "Starting" : "Rescan All"}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {accounts.length === 0 ? (
        <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED, py: 1 }}>This assignment has no accounts.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {accounts.map((account) => {
            const scopes = Array.isArray(account.scopes) ? account.scopes : [];
            const regions = Array.isArray(account.regions) ? account.regions.filter(Boolean) : [];
            const label = accountLabelOf(account);
            const isScanning = Boolean(scanning[account.auditAccountId]);
            return (
              <Box
                key={account.auditAccountId || account.accountNumber}
                sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "8px", backgroundColor: AH_WHITE, px: 1.5, py: 1.25 }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: AH_TEXT, overflowWrap: "anywhere" }}>
                        {label}
                      </Typography>
                      {isScanning ? (
                        <Box
                          role="status"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.75,
                            height: 20,
                            borderRadius: "6px",
                            backgroundColor: "#fff4eb",
                            color: "#9a3412",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                          }}
                        >
                          <CircularProgress size={10} sx={{ color: AH_ACCENT }} />
                          Scanning
                        </Box>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED, mt: 0.25, overflowWrap: "anywhere" }}>
                      {account.accountNumber}
                      {regions.length ? ` · ${regions.join(", ")}` : ""}
                    </Typography>
                  </Box>
                  {editable || canRescan ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      {canRescan ? (
                        <Tooltip title="Rescan Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => rescan(account)}
                              aria-label={`Rescan ${label}`}
                              sx={ACTION_BTN_SX}
                            >
                              {rescanning === account.auditAccountId ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <RefreshIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {editable ? (
                        <Tooltip title="Remove Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => setRemoveTarget({ account })}
                              aria-label={`Remove ${label}`}
                              sx={DELETE_BTN_SX}
                            >
                              <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
                {scopes.length === 0 ? (
                  <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED, mt: 1 }}>No frameworks on this account.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {scopes.map((scope) => {
                      const name = frameworkLabel(scope.framework);
                      return (
                        <Tooltip key={scope.auditScopeId} title={scopeTooltip(scope)} placement="top">
                          <Chip
                            size="small"
                            label={name}
                            onDelete={
                              editable && !busy && scopes.length > 1 ? () => setRemoveTarget({ account, scope }) : undefined
                            }
                            deleteIcon={<CancelIcon aria-label={`Remove ${name} from ${label}`} />}
                            sx={scopeChipSx}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <AddAccountDialog
        auditId={auditId}
        existingAccountNumbers={accounts.map((account) => account.accountNumber)}
        frameworks={auditFrameworks}
        isLive={canRescan}
        open={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
        onAdded={({ account, result }) => {
          setAddAccountOpen(false);
          const label = accountLabelOf(account);
          const runId = result?.scan?.runId;
          if (runId && result?.auditAccountId) {
            scanStartedAt.current = Date.now();
            setScanning((prev) => ({ ...prev, [result.auditAccountId]: runId }));
          }
          if (result?.scanDispatchFailed) {
            setNotice({
              severity: "warning",
              message: `Added ${label}, but its scan could not be started. Use Rescan Account to try again.`,
            });
            reload();
            if (onChanged) onChanged();
            return;
          }
          afterChange(runId ? `Added ${label}. Its scan has started.` : `Added ${label}.`);
        }}
      />
      <RemoveScopeDialog
        auditId={auditId}
        target={removeTarget}
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onRemoved={({ account, scope, result }) => {
          setRemoveTarget(null);
          const destroyed = Number(result?.decidedControlsDestroyed) || 0;
          const removed = Number(result?.scopesRemoved) || 0;
          const base = scope
            ? `Removed ${frameworkLabel(scope.framework)} from ${accountLabelOf(account)}.`
            : `Removed ${accountLabelOf(account)}${removed ? ` and ${plural(removed, "framework")}` : ""} from this assignment.`;
          afterChange(destroyed ? `${base} ${plural(destroyed, "decided control")} deleted.` : base);
        }}
      />
    </Box>
  );
};

export default AuditScopeSection;
